import {
    HOTELS_UPSTREAM_BASE_URL,
    HOTEL_AUTO_SUGGEST_PATH,
    HOTEL_AUTO_SUGGEST_USE_CASE
} from '@/src/constants/hotels'
import {
    HotelAutoSuggestItem,
    HotelAutoSuggestRequest,
    PrefixyAutoSuggestResponse,
    PrefixySuggestion
} from '@/src/types/hotels/autoSuggest'
import { joinSegments } from '@/src/utils/hotels'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const toItem = (suggestion: PrefixySuggestion): HotelAutoSuggestItem | null => {
    const isHotel = suggestion.suggestionType === 'HOTEL'
    const isCity = suggestion.suggestionType === 'CITY'
    // LOCALITY is the only other type, and it has no searchable equivalent yet.
    if (!isHotel && !isCity) return null

    const name = (isHotel ? suggestion.hotelName : suggestion.cityName)?.trim() || ''
    if (!name) return null

    const location = joinSegments([suggestion.cityName, suggestion.stateName, suggestion.countryName])

    return {
        kind: isHotel ? 'hotel' : 'city',
        // A property leads with its name and puts the location underneath; a city has only
        // its location to show, so that becomes the top line and there is no second line.
        primary: isHotel ? name : location,
        secondary: isHotel ? location : '',
        label: joinSegments([name, suggestion.cityName, suggestion.stateName, suggestion.countryName]),
        cityId: suggestion.cityId || '',
        cityName: suggestion.cityName || '',
        stateId: suggestion.stateId || '',
        stateName: suggestion.stateName || '',
        countryId: suggestion.countryId || '',
        countryName: suggestion.countryName || '',
        countryCode: suggestion.countryCode || '',
        hotelId: suggestion.hotelId || '',
        hotelName: suggestion.hotelName || '',
        // Only meaningful for a city; a hotel's `bookingCount` is not shown.
        propertyCount: isCity && typeof suggestion.propertyCount === 'number' ? suggestion.propertyCount : null
    }
}

/**
 * Cleartrip destination autosuggest.
 *
 * Deliberately does not use `createHotelBaseQuery`: that attaches the Flyin upstream's
 * contract headers (`new-native-app`, `domain: MAPP`, `api`, `device`), which have no
 * meaning for a Cleartrip service. Uses the service-wide `setCommonHeaders` instead, so
 * `app-agent` follows the real device rather than being pinned to one value.
 *
 * Cached per prefix, and the results never go stale within a session — the same prefix
 * typed again is served from cache with no request.
 */
export const hotelAutoSuggestApi = createApi({
    reducerPath: 'hotelAutoSuggestApi',
    baseQuery: fetchBaseQuery({
        baseUrl: HOTELS_UPSTREAM_BASE_URL,
        prepareHeaders: headers => setCommonHeaders(headers)
    }),

    keepUnusedDataFor: 600,

    endpoints: builder => ({
        getHotelAutoSuggest: builder.query<HotelAutoSuggestItem[], HotelAutoSuggestRequest>({
            query: ({ prefix }) => ({
                url: HOTEL_AUTO_SUGGEST_PATH,
                method: 'POST',
                body: { useCaseContext: HOTEL_AUTO_SUGGEST_USE_CASE, prefix }
            }),
            // Cleartrip interleaves cities and properties in one pre-ranked array. Cities are
            // hoisted above properties here — a city covers the whole destination, so it is
            // the more useful first result — and Cleartrip's ranking is preserved within each
            // group. `topPlaces` is ignored: its entries carry no ids to search on.
            transformResponse: (response: PrefixyAutoSuggestResponse): HotelAutoSuggestItem[] => {
                const items = (response?.suggestions || [])
                    .map(toItem)
                    .filter((item): item is HotelAutoSuggestItem => item !== null)

                return [
                    ...items.filter(item => item.kind === 'city'),
                    ...items.filter(item => item.kind === 'hotel')
                ]
            }
        })
    })
})

export const { useGetHotelAutoSuggestQuery } = hotelAutoSuggestApi

export default hotelAutoSuggestApi
